import { captureException } from '@sentry/browser'

import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, inject, signal } from '@angular/core'

import { MatSnackBar } from '@angular/material/snack-bar'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjanpitoService, ListausAlvIlmoitus } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { Asiakas, AsiakkaalleLemonatoristaLahetettyLasku, KirjanpitoKuukausiruutu, VerojenMaksuyhteystiedot } from 'app/_jaettu-lemonator/model/asiakas'
import {
  AlvIlmoituksenLahetyspyynto,
  AlvIlmoituksenTunnistetiedot,
  AlvIlmoituksenTyviTiedostonLatauspyynto,
  AlvIlmoituksenTyviTiedostonLatausvastaus,
  AlvIlmoitus,
  AlvTilienArhPaattaminenIlmoituksenPerusteellaPyynto,
  AlvTilienPaattaminenPyynto,
  AlvTilienPaattaminenVastaus,
} from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { KirjanpitoJaettuService } from '../_jaettu-lemonator/service/kirjanpito-jaettu.service'

import { LocalDate, LocalMonth } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { disabled, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { Lasku } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LEMONATOR_CF_API, LemonHttpService } from 'app/_angular/service/lemon-http.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AlvLaskunLahetysService } from 'app/_jaettu-lemonator/service/alv-laskun-lahetys.service'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { LaskuLataaLahetystiedostaDialog, LaskuLataaLahetystiedostaDialogData } from 'app/laskut/dialogit/lasku.lataa-lahetystiedosta.dialog'
import { LaskuLataaDialog, LaskuLataaDialogData } from 'app/laskut/dialogit/lasku.lataa.dialog'

interface AlvLaskuLomake {
  maara: number | null
  send: boolean
}

interface AlarajahuojennusLomake {
  arhVero: number | null
  arhLiikevaihto: number | null
}

@Component({
  selector: '[app-kirjanpito-alv-ilmoitus]',
  templateUrl: './alv-ilmoitus.component.html',
  styleUrls: ['./alv-ilmoitus.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoAlvIlmoitusComponent implements OnInit, OnDestroy {
  @Input() valittuKuukausiObservable: Observable<LocalMonth>
  @Output() nykyisellaKuukaudellaOnMuutoksia: EventEmitter<'yes' | 'no' | 'not-saved-yet'> = new EventEmitter()
  @Output() viimeisinAlvIlmoitus: EventEmitter<AlvIlmoitus> = new EventEmitter()

  private _ngUnsubscribe = new Subject<void>()

  tunnistetietoObservable: Observable<string>
  tallennustiedotObservable: Observable<string>
  aikaisemmatVersiotObservable: Observable<ListausAlvIlmoitus[]>

  historyLoadingSubject = new BehaviorSubject<boolean>(true)
  numbersLoadingSubject = new BehaviorSubject<boolean>(true)
  alvIlmoitusSubject = new BehaviorSubject<AlvIlmoitus>(this._kirjanpitoJaettuService.annaUusiAlvilmoitus())
  naytaArhObservable = combineLatest([this.alvIlmoitusSubject, this._asiakasService.nykyinenAsiakasObservable]).pipe(
    map(([alvIlmoitus, asiakas]) => {
      if (alvIlmoitus?.endDate && asiakas?.tilikaudet) {
        const tilikausi = this._kirjanpitoJaettuService.annaTilikausiPaivalle(asiakas.tilikaudet, alvIlmoitus.endDate)
        return tilikausi && tilikausi.alkaa.year < 2025
      }
      return false
    }),
  )

  tallentamattomiaMuutoksiaObservable: Observable<boolean>
  edellinenAlvIlmoitusObservable: Observable<AlvIlmoitus | null>
  edellinenMaksettavaTaiPalautettavaVeroObservable: Observable<number>
  maksettavaTaiPalautettavaVeroObservable: Observable<number>
  muutosVeronMaarassaObservable: Observable<number>
  voidaanLahettaaObservable: Observable<boolean>
  voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable: Observable<boolean>
  errorMessageObservable: Observable<string>
  draftFirestoreLinkObservable: Observable<{ uri: string; url: string } | null>

  alvLaskunViitenumeroObservable: Observable<string>
  alvLaskunSahkoinenLaskutusosoiteKunnossaObservable: Observable<boolean>
  asiakkaanOsoitetiedotPuuttuvatObservable: Observable<boolean>
  alvLaskunPerustiedotKunnossaObservable: Observable<boolean>
  private _alvLaskunVoiLahettaaObservable: Observable<boolean>
  // aikaisemminLahetetytLaskutObservable: Observable<AsiakkaanMaksettuLaskuJaEsikatselutiedot[]>
  // laskunEsikatselutiedotObservable: Observable<LaskuPdfEsikatselutiedot>

  naidenErotus: number = 0
  naytaArh: boolean = false
  arhVirhe: string = null
  // onkoAlvTilienPaattoJoTehtyObservable: Observable<boolean>

  private readonly _injector = inject(Injector)
  private readonly _alvLaskunMaaraPoissa = signal(false)
  private readonly _alvLaskunLahetysPoissa = signal(false)

  alvLaskuLomake = form(signal<AlvLaskuLomake>({ maara: 0, send: false }), polku => {
    required(polku.maara)
    disabled(polku.maara, () => this._alvLaskunMaaraPoissa())
    disabled(polku.send, () => this._alvLaskunLahetysPoissa())
  })

  arhLomake = form(signal<AlarajahuojennusLomake>({ arhVero: null, arhLiikevaihto: null }), polku => {
    required(polku.arhVero)
    required(polku.arhLiikevaihto)
  })

  constructor(
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _currencyService: CurrencyService,
    private _asiakasService: AsiakasService,
    private _asiakasUriService: AsiakasUriService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _dateService: DateService,
    private _ladataanService: LadataanService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjanpitoService: KirjanpitoService,
    private _kirjanpitoJaettuService: KirjanpitoJaettuService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _snackbar: MatSnackBar,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _viitenumeroService: ViitenumeroService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _laskuUriService: LaskuUriService,
    private _httpService: LemonHttpService,
    private _timestampService: TimestampService,
    private _fileSaverService: FileSaverService,
    private _debugService: DebugService,
  ) {}

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {
    const hyvaksymattomatKirjauksetRaakadataObservable: Observable<KirjanpitoKuukausiruutu[]> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const collectionUri = this._asiakasUriService.annaListausRuutuCollectionUri(asiakas.avain)
          return this._firebase.firestoreCollection<KirjanpitoKuukausiruutu>(collectionUri).where('u', '>', 0).listen()
        }
        return of<KirjanpitoKuukausiruutu[]>([])
      }),
    )

    const hyvaksymattomatKirjauksetAlvJaksonAikanaObservable: Observable<KirjanpitoKuukausiruutu[]> = combineLatest([
      this._asiakasService.nykyinenAsiakasObservable,
      this.valittuKuukausiObservable,
      hyvaksymattomatKirjauksetRaakadataObservable,
    ]).pipe(
      map(([asiakas, kuukausi, ruudut]) => {
        if (asiakas && kuukausi && ruudut) {
          const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
          const alkuPvm = jaksotiedot ? this._dateService.localMonthToNumber(jaksotiedot.aikavali.start) : 0
          const loppuPvm = jaksotiedot ? this._dateService.localMonthToNumber(jaksotiedot.aikavali.end) : 0
          return ruudut.filter(a => {
            return alkuPvm <= a.k && a.k <= loppuPvm
          })
        }
        return ruudut
      }),
    )

    const perustiedotObservable: Observable<{ asiakas: Asiakas; kuukausi: LocalMonth; tunnistetiedot: AlvIlmoituksenTunnistetiedot }> = combineLatest([
      this._asiakasService.nykyinenAsiakasObservable,
      this.valittuKuukausiObservable,
    ]).pipe(
      map(([asiakas, kuukausi]) => {
        if (asiakas && kuukausi) {
          const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
          const tunnistetiedot = this._kirjanpitoJaettuService.annaAlvilmoituksenTunnistetiedot(jaksotiedot)
          if (tunnistetiedot) {
            return { asiakas: asiakas, kuukausi: kuukausi, tunnistetiedot: tunnistetiedot }
          }
        }
        return null
      }),
      // ,
      // distinctUntilChanged((previous, current) => {
      //   if (!previous && !!current) {
      //     return false
      //   }
      //   if (!!previous && !current) {
      //     return false
      //   }
      //   if (!previous && !current) {
      //     return true
      //   }
      //   if (previous.asiakas?.avain !== current.asiakas?.avain) {
      //     return false
      //   }
      //   if (previous.kuukausi?.year !== current.kuukausi?.year) {
      //     return false
      //   }
      //   if (previous.kuukausi?.month !== current.kuukausi?.month) {
      //     return false
      //   }
      //   if (previous.tunnistetiedot?.vuosi !== current.tunnistetiedot?.vuosi) {
      //     return false
      //   }
      //   if (previous.tunnistetiedot?.jarjestys !== current.tunnistetiedot?.jarjestys) {
      //     return false
      //   }
      //   if (previous.tunnistetiedot?.alvIlmoitusjakso !== current.tunnistetiedot?.alvIlmoitusjakso) {
      //     return false
      //   }
      //   return true
      // })
    )

    this.draftFirestoreLinkObservable = combineLatest([this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable, perustiedotObservable]).pipe(
      map(([kirjanpitaja, perustiedot]) => {
        if (kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER && perustiedot) {
          const uri = this._kirjanpitoUriService.annaAlvIlmoituksenDraftUri(perustiedot.asiakas.avain, perustiedot.tunnistetiedot)
          return { uri, url: this._debugService.createFirestoreLink(uri) }
        }
        return null
      }),
    )

    // If the asiakas or kuukausi changes, we need to reset the form.
    perustiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(perustiedot => {
      asetaArvo(this.arhLomake.arhVero, null, { ilmoita: false })
      asetaArvo(this.arhLomake.arhLiikevaihto, null, { ilmoita: false })
    })

    combineLatest([perustiedotObservable, arvonMuutokset(this.arhLomake.arhVero, { injector: this._injector }), arvonMuutokset(this.arhLomake.arhLiikevaihto, { injector: this._injector })])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([perustiedot, vero, liikevaihto]) => {
        const ilmoitus = this.alvIlmoitusSubject.value
        if (ilmoitus && perustiedot) {
          const tilikausi = this._kirjanpitoJaettuService.annaTilikausiKuukaudenEnsimmaisellePaivalle(perustiedot.asiakas.tilikaudet, perustiedot.kuukausi)

          if (vero || liikevaihto) {
            if (!ilmoitus.arhSyotetty) {
              ilmoitus.arhSyotetty = {}
            }

            if (vero) {
              ilmoitus.arhSyotetty.vero = vero
            } else {
              delete ilmoitus.arhSyotetty.vero
            }
            if (liikevaihto) {
              ilmoitus.arhSyotetty.suhteutettuLiikevaihto = liikevaihto
            } else {
              delete ilmoitus.arhSyotetty.suhteutettuLiikevaihto
            }
            if (tilikausi && vero && liikevaihto) {
              ilmoitus.arhSyotetty.huojennus = this._kirjanpitoJaettuService.laskeAlarajahuojennus(tilikausi, vero, liikevaihto)
            } else {
              delete ilmoitus.arhSyotetty.huojennus
            }

            this.alvIlmoitusSubject.next(this.alvIlmoitusSubject.value)
          } else {
            delete ilmoitus.arhSyotetty
            asetaArvo(this.arhLomake.arhVero, null, { ilmoita: false })
            asetaArvo(this.arhLomake.arhLiikevaihto, null, { ilmoita: false })
            this.alvIlmoitusSubject.next(this.alvIlmoitusSubject.value)
          }
        } else {
          asetaArvo(this.arhLomake.arhVero, null, { ilmoita: false })
          asetaArvo(this.arhLomake.arhLiikevaihto, null, { ilmoita: false })
        }
        this.arhVirhe = null
      })

    // this.onkoAlvTilienPaattoJoTehtyObservable = perustiedotObservable.pipe(
    //   switchMap(perustiedot => {
    //     if (perustiedot) {
    //       const tunniste = this._kirjanpitoJaettuService.annaAlvilmoituksenTunniste(perustiedot.tunnistetiedot)
    //       const kirjaus1Avain = 'alv_automaattikirjaus1_' + tunniste
    //       const kirjaus1Uri = this._kirjanpitoUriService.annaRaportointikirjauksenUri(perustiedot.asiakas.avain, kirjaus1Avain)
    //       return this._firestore.doc<Raportointikirjaus>(kirjaus1Uri).valueChanges()
    //     }
    //     return of<Raportointikirjaus>(null)
    //   }),
    //   map(kirjaus => !!kirjaus)
    // )

    const aikaisemmatAlvIlmoituksetObservable = perustiedotObservable.pipe(
      tap(() => this.historyLoadingSubject.next(true)),
      switchMap(perustiedot => {
        if (perustiedot) {
          const tunniste = this._kirjanpitoJaettuService.annaAlvilmoituksenTunniste(perustiedot.tunnistetiedot)
          const alvilmoituksetUri = this._kirjanpitoUriService.annaAlvIlmoituksenCollectionUri(perustiedot.asiakas.avain)
          console.log('Haetaan tunnisteella 1: ' + tunniste + ' urista ' + alvilmoituksetUri)
          return this._firebase.firestoreCollection<AlvIlmoitus>(alvilmoituksetUri).where('tunniste', '==', tunniste).listen()
        }
        return of<AlvIlmoitus[]>([])
      }),
      map(edelliset => {
        return edelliset.sort((a, b) => b.luotu.toMillis() - a.luotu.toMillis())
      }),
      takeUntil(this._ngUnsubscribe),
      lemonShare(),
    )

    this.edellinenAlvIlmoitusObservable = aikaisemmatAlvIlmoituksetObservable.pipe(
      map(edelliset => {
        if (edelliset && edelliset.length > 0) {
          const sorted = [...edelliset].sort((a, b) => b.luotu.toMillis() - a.luotu.toMillis())
          return sorted[0]
        }
        return null
      }),
    )

    // This check system needs only input from asiakas and kuukausi, but we also want to execute it when
    // edelliset changes
    combineLatest([this._asiakasService.nykyinenAsiakasObservable, this.valittuKuukausiObservable, aikaisemmatAlvIlmoituksetObservable])
      .pipe(
        switchMap(([asiakas, kuukausi, aikaisemmat]) => {
          if (asiakas && kuukausi) {
            const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
            const tunnistetiedot = this._kirjanpitoJaettuService.annaAlvilmoituksenTunnistetiedot(jaksotiedot)
            if (tunnistetiedot) {
              const tunniste = this._kirjanpitoJaettuService.annaAlvilmoituksenTunniste(tunnistetiedot)
              const alvilmoituksetUri = this._kirjanpitoUriService.annaAlvIlmoituksenCollectionUri(asiakas.avain)
              console.log('Haetaan tunnisteella 2: ' + tunniste + ' urista ' + alvilmoituksetUri)
              return this._firebase.firestoreCollection<AlvIlmoitus>(alvilmoituksetUri).where('tunniste', '==', tunniste).getFromServer()
            }
          }
          return of<AlvIlmoitus[]>([])
        }),
        map(edelliset => {
          if (edelliset) {
            return edelliset.sort((a, b) => b.luotu.toMillis() - a.luotu.toMillis())
          }
          return null
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(edelliset => {
        if (!edelliset) {
          return
        }

        let index = 0
        for (const ilmoitus of edelliset) {
          const edellinen = index + 1 < edelliset.length ? edelliset[index + 1] : null
          if (edellinen) {
            if (ilmoitus.edellinenMaksettavaTaiPalautettavaVero !== this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(edellinen)) {
              captureException(
                new Error(
                  'Edellisen maksetun veron määrä ei täsmää ilmoituksessa 1 ' +
                    ilmoitus.avain +
                    '! ' +
                    ilmoitus.edellinenMaksettavaTaiPalautettavaVero +
                    ' !== ' +
                    this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(edellinen),
                ),
              )
            }
          } else if (ilmoitus.edellinenMaksettavaTaiPalautettavaVero !== 0) {
            // console.log('Edellisen maksetun veron määrä ei täsmää ilmoituksessa ' + alvIlmoitus.avain + '! ' + alvIlmoitus.edellinenMaksettavaTaiPalautettavaVero + ' !== 0,00')
            captureException(new Error('Edellisen maksetun veron määrä ei täsmää ilmoituksessa 2 ' + ilmoitus.avain + '! ' + ilmoitus.edellinenMaksettavaTaiPalautettavaVero + ' !== 0,00'))
          }
          index++
        }
      })

    this.edellinenMaksettavaTaiPalautettavaVeroObservable = this.edellinenAlvIlmoitusObservable.pipe(
      map(edellinen => {
        if (edellinen) {
          return this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(edellinen)
        }
        return 0
      }),
    )

    // Emit latest sent ALV-ilmoitus or newly created one (if none sent yet)
    combineLatest([this.edellinenAlvIlmoitusObservable, this.alvIlmoitusSubject])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([edellinenIlmoitus, nykyinenlmoitus]) => {
        if (edellinenIlmoitus) {
          this.viimeisinAlvIlmoitus.emit(edellinenIlmoitus)
        } else {
          this.viimeisinAlvIlmoitus.emit(nykyinenlmoitus)
        }
      })

    this.maksettavaTaiPalautettavaVeroObservable = this.alvIlmoitusSubject.pipe(
      map(alvIlmoitus => {
        return this._kirjanpitoJaettuService.annaMaksettavaTaiPalautettavaVero(alvIlmoitus)
      }),
    )

    this.muutosVeronMaarassaObservable = combineLatest([this.maksettavaTaiPalautettavaVeroObservable, this.edellinenMaksettavaTaiPalautettavaVeroObservable]).pipe(
      map(([maksettavaTaiPalautettavaVero, edellinenMaksettavaTaiPalautettavaVero]) => {
        return maksettavaTaiPalautettavaVero - edellinenMaksettavaTaiPalautettavaVero
      }),
    )

    this.aikaisemmatVersiotObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this._asiakasService.nykyinenAsiakasAvainObservable,
      aikaisemmatAlvIlmoituksetObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
    ]).pipe(
      map(([kirjanpitaja, asiakas, ilmoitukset, kirjanpitajienNimitiedotMap]) => {
        if (!kirjanpitaja || !asiakas || !ilmoitukset || !kirjanpitajienNimitiedotMap) {
          return []
        }
        return ilmoitukset.map(ilmoitus => {
          return this._kirjanpitoService.muunnaAlvIlmoitusListausAlvIlmoitukseksi(kirjanpitaja.rooli === KirjanpitajanRooli.SUPER, asiakas, ilmoitus, kirjanpitajienNimitiedotMap)
        })
      }),
      tap(() => this.historyLoadingSubject.next(false)),
    )

    this.tallentamattomiaMuutoksiaObservable = combineLatest([this.alvIlmoitusSubject, this.edellinenAlvIlmoitusObservable]).pipe(
      map(([alvIlmoitus, edellinenAlvIlmoitus]) => {
        return this._kirjanpitoJaettuService.onkoAlvIlmoituksissaTaiArhssaEroja(alvIlmoitus, edellinenAlvIlmoitus)
      }),
    )

    this.voidaanLahettaaObservable = combineLatest([this.tallentamattomiaMuutoksiaObservable, hyvaksymattomatKirjauksetAlvJaksonAikanaObservable]).pipe(
      map(([tallentamattomiaMuutoksia, hyvaksymattomatKirjauksetAlvJaksonAikana]) => {
        return hyvaksymattomatKirjauksetAlvJaksonAikana.length < 1 && tallentamattomiaMuutoksia
      }),
    )

    this.voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this.tallentamattomiaMuutoksiaObservable,
      hyvaksymattomatKirjauksetAlvJaksonAikanaObservable,
    ]).pipe(
      map(([kirjanpitaja, tallentamattomiaMuutoksia, hyvaksymattomatKirjauksetAlvJaksonAikana]) => {
        return kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER && hyvaksymattomatKirjauksetAlvJaksonAikana.length < 1 && !tallentamattomiaMuutoksia
      }),
    )

    combineLatest([this.tallentamattomiaMuutoksiaObservable, this.aikaisemmatVersiotObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([onkoMuutoksia, aikaisemmatVersiot]) => {
        if (aikaisemmatVersiot && aikaisemmatVersiot.length > 0) {
          this.nykyisellaKuukaudellaOnMuutoksia.next(onkoMuutoksia ? 'yes' : 'no')
        } else {
          this.nykyisellaKuukaudellaOnMuutoksia.next('not-saved-yet')
        }
      })

    const draftObservable: Observable<AlvIlmoitus> = perustiedotObservable.pipe(
      switchMap(perustiedot => {
        if (perustiedot) {
          const draftUri = this._kirjanpitoUriService.annaAlvIlmoituksenDraftUri(perustiedot.asiakas.avain, perustiedot.tunnistetiedot)
          return this._firebase.firestoreDoc<AlvIlmoitus>(draftUri).listen()
        }
        return of<AlvIlmoitus>(null)
      }),
    )

    combineLatest([draftObservable, this.edellinenAlvIlmoitusObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([draft, edellinen]) => {
        if (!draft) {
          this.numbersLoadingSubject.next(false)
          this.alvIlmoitusSubject.next(this._kirjanpitoJaettuService.annaUusiAlvilmoitus())
          return
        }

        this.naidenErotus = this._currencyService.roundHalfUp(draft.automaattikirjaus2939Summa - draft.automaattikirjaus1763Summa, 2)
        this.numbersLoadingSubject.next(false)
        this.alvIlmoitusSubject.next(draft)

        if (edellinen?.arhSyotetty) {
          asetaArvo(this.arhLomake.arhVero, edellinen.arhSyotetty.vero)
          asetaArvo(this.arhLomake.arhLiikevaihto, edellinen.arhSyotetty.suhteutettuLiikevaihto)
        } else {
          asetaArvo(this.arhLomake.arhVero, null)
          asetaArvo(this.arhLomake.arhLiikevaihto, null)
        }
      })

    this.tunnistetietoObservable = perustiedotObservable.pipe(
      map(perustiedot => {
        if (perustiedot) {
          return this._kirjanpitoJaettuService.annaTunnistetiedotString(perustiedot.tunnistetiedot, 'fi')
        }
        return ''
      }),
    )

    this.errorMessageObservable = combineLatest([this.tunnistetietoObservable, hyvaksymattomatKirjauksetAlvJaksonAikanaObservable]).pipe(
      map(([tunnistetiedot, hyvaksymattomatRuudut]) => {
        if (hyvaksymattomatRuudut?.length && tunnistetiedot) {
          let kuukaudet = ''
          for (const ruutu of hyvaksymattomatRuudut) {
            const month = this._dateService.numberToLocalMonth(ruutu.k)
            kuukaudet += ' ' + month.month + '/' + month.year + ','
          }
          kuukaudet = kuukaudet.substring(0, kuukaudet.length - 1)
          return 'Et voi lähettää ALV-ilmoitusta ennen kuin olet hyväksynyt kaikki verokauden ' + tunnistetiedot + ' kirjaukset. Hyväksymättömiä kirjauksia löytyi kuukausilta:' + kuukaudet + '.'
        }
        return null
      }),
    )

    const sahkoisetValitystiedotObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const maksutietojenPath = 'customers/' + asiakas.avain + '/apix-received-invoice-config/' + asiakas.avain
          return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>(maksutietojenPath).listen()
        }
        return of<ApixReceivedInvoiceConfig>(null)
      }),
      lemonShare(),
    )

    const yhteystietoObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const maksutietojenPath = 'customers/' + asiakas.avain + '/customer-vero-maksuyhteystiedot/' + asiakas.avain
          return this._firebaseLemonaid.firestoreDoc<VerojenMaksuyhteystiedot>(maksutietojenPath).listen()
        }
        return of<VerojenMaksuyhteystiedot>(null)
      }),
      lemonShare(),
    )

    this.alvLaskunViitenumeroObservable = yhteystietoObservable.pipe(
      map(yhteystiedot => {
        const yleinenViite = yhteystiedot?.yleinenViite?.trim()
        if (yleinenViite && this._viitenumeroService.onkoViitenumeroValidi(yleinenViite)) {
          return yleinenViite
        }

        const omaViite = yhteystiedot?.omaAloitteisetViite?.trim()
        if (omaViite && this._viitenumeroService.onkoViitenumeroValidi(omaViite)) {
          return omaViite
        }
        return ''
      }),
    )

    this.alvLaskunSahkoinenLaskutusosoiteKunnossaObservable = combineLatest([sahkoisetValitystiedotObservable, this._asiakasService.nykyinenAsiakasObservable]).pipe(
      map(([sahkoisetValitystiedot, asiakas]) => {
        if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.paymentReceiveIsActive) {
          // Jos maksut on asiakkaalla käytössä -> menee aina maksuihin.
          return true
        } else if (sahkoisetValitystiedot && sahkoisetValitystiedot.forwardLemontreeLaskut && sahkoisetValitystiedot.forwardActive) {
          if (!sahkoisetValitystiedot.forwardAddress || !sahkoisetValitystiedot.forwardAddress.sahkoinenOsoite || !sahkoisetValitystiedot.forwardAddress.sahkoinenValittaja) {
            throw new Error('Asiakkaalle asiakkaat/' + asiakas.avain + ' on merkitty välitys aktiiviseksi ja lemontreen laskujen välitys päälle, mutta sähköistä osoitetta ei löydy.')
          }
          return true
        } else if (asiakas && asiakas.sahkoinenLaskutusosoite && asiakas.sahkoinenLaskutusosoite.sahkoinenOsoite && asiakas.sahkoinenLaskutusosoite.sahkoinenValittaja) {
          return true
        }
        return false
      }),
    )

    this.asiakkaanOsoitetiedotPuuttuvatObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        return !asiakas?.katuosoite || !asiakas?.postitmp || !asiakas?.postinro
      }),
    )

    this.alvLaskunPerustiedotKunnossaObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasObservable,
      this.alvLaskunSahkoinenLaskutusosoiteKunnossaObservable,
      yhteystietoObservable,
      this.asiakkaanOsoitetiedotPuuttuvatObservable,
    ]).pipe(
      map(([asiakas, sahkoinenLaskutusosoiteKunnossa, yhteystiedot, asiakkaanOsoitetiedotPuuttuvat]) => {
        const yleinenViite = yhteystiedot?.yleinenViite?.trim()
        const yleinenViiteOnValidi = yleinenViite && this._viitenumeroService.onkoViitenumeroValidi(yleinenViite)

        const omaViite = yhteystiedot?.omaAloitteisetViite?.trim()
        const omaViiteOnValidi = omaViite && this._viitenumeroService.onkoViitenumeroValidi(omaViite)

        const viiteOnValidi = yleinenViiteOnValidi || omaViiteOnValidi

        if (!asiakas.estaAlvVerkkolaskujenLahetys && sahkoinenLaskutusosoiteKunnossa && yhteystiedot && viiteOnValidi && !asiakkaanOsoitetiedotPuuttuvat) {
          return true
        }
        return false
      }),
      lemonShare(),
    )

    this._alvLaskunVoiLahettaaObservable = combineLatest([this.alvLaskunPerustiedotKunnossaObservable, this.muutosVeronMaarassaObservable]).pipe(
      map(([perustiedotKunnossa, alvIlmoituksenSumma]) => {
        if (perustiedotKunnossa && alvIlmoituksenSumma > 0) {
          return true
        }
        return false
      }),
    )

    this.alvLaskunPerustiedotKunnossaObservable.pipe(distinctUntilChanged(), takeUntil(this._ngUnsubscribe)).subscribe(isAvailable => {
      if (isAvailable) {
        this._alvLaskunMaaraPoissa.set(false)
      } else {
        this._alvLaskunMaaraPoissa.set(true)
      }
    })

    this._alvLaskunVoiLahettaaObservable.pipe(distinctUntilChanged(), takeUntil(this._ngUnsubscribe)).subscribe(isAvailable => {
      asetaArvo(this.alvLaskuLomake.send, !!isAvailable)
      if (isAvailable) {
        this._alvLaskunLahetysPoissa.set(false)
        // this.alvLaskuMaaraCtrl.enable()
      } else {
        this._alvLaskunLahetysPoissa.set(true)
        // this.alvLaskuMaaraCtrl.disable()
      }
    })

    this.muutosVeronMaarassaObservable.pipe(distinctUntilChanged(), takeUntil(this._ngUnsubscribe)).subscribe(muutos => {
      if (muutos > 0) {
        asetaArvo(this.alvLaskuLomake.maara, muutos)
      } else {
        asetaArvo(this.alvLaskuLomake.maara, 0)
      }
    })
  }

  async lataaAlvLasku(ilmoitus: ListausAlvIlmoitus) {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const lahetystieto = await this._firebaseLemonaid.firestoreDoc<AsiakkaalleLemonatoristaLahetettyLasku>('customers/' + asiakas.avain + '/customer-sent-vat-invoice/' + ilmoitus.avain).get()

    if (!lahetystieto) {
      const data: LaskuLataaLahetystiedostaDialogData = {
        alvIlmoituksenAvain: ilmoitus.avain,
        asiakasAvain: asiakas.avain,
      }
      this._dialog.open(LaskuLataaLahetystiedostaDialog, { data: data, panelClass: 'ilman-paddingia' })
    } else {
      const laskuUri = lahetystieto ? this._laskuUriService.getCustomerLaskuUri(AlvLaskunLahetysService.lahettavanAsiakkaanAsiakasAvain, lahetystieto.laskuAvain) : null
      const lasku = laskuUri ? await this._firebaseLemonaid.firestoreDoc<Lasku>(laskuUri).get() : null

      if (lasku && ((lasku.print && lasku.print.done) || (lasku.email && lasku.email.done) || (lasku.sahkoinen && lasku.sahkoinen.done))) {
        this._ladataanService.aloitaLataaminen()
        const pdfUri = this._laskuUriService.getPdfUri(AlvLaskunLahetysService.lahettavanAsiakkaanAsiakasId, lahetystieto.laskuAvain)
        const url = '/api/1/laskut/lataaPdf/' + pdfUri
        return this._httpService
          .getBinary('/laskuLataaPdf?a=' + encodeURIComponent(url) + '&time=' + encodeURIComponent(new Date().getTime()), LEMONATOR_CF_API)
          .then(result => {
            const nimi = 'alv-lasku.pdf'
            this._ladataanService.lopetaLataaminen()
            this._fileSaverService.saveAs(result, nimi)
          })
          .catch(err => {
            this._ladataanService.lopetaLataaminen()
            this._errorHandler.handleError(err)
          })
      } else {
        const data: LaskuLataaDialogData = {
          juurilasku: lasku,
          kasiteltava: lasku,
        }
        this._dialog.open(LaskuLataaDialog, { data: data, panelClass: 'ilman-paddingia' })
      }
    }
  }

  toggleArh() {
    if (this.naytaArh) {
      this.naytaArh = false
      asetaArvo(this.arhLomake.arhVero, null)
      asetaArvo(this.arhLomake.arhLiikevaihto, null)
    } else {
      this.naytaArh = true
    }
  }

  async laskeAutomaattikirjauksetUudelleen() {
    this._ladataanService.aloitaLataaminen()

    const [asiakas, kuukausi] = await Promise.all([firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable), firstValueFrom(this.valittuKuukausiObservable)])
    const data: AlvTilienPaattaminenPyynto = {
      asiakasAvain: asiakas.avain,
      kuukausi: kuukausi,
    }
    return this._firebase
      .functionsCall<AlvTilienPaattaminenPyynto, AlvTilienPaattaminenVastaus>('kirjanpitoAlvIlmoitusAutomaattikirjauksetUusiUudelleen', data)
      .then(result => {
        this._ladataanService.lopetaLataaminen()
        this._snackbar.open('ALV-tilit päätettiin onnistuneesti.', 'OK', { duration: 2500 })
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this._ladataanService.lopetaLataaminen()
      })
  }

  async laskeArhAutomaattikirjauksetUudelleen() {
    this._ladataanService.aloitaLataaminen()

    const [asiakas, edellinen] = await Promise.all([firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable), firstValueFrom(this.edellinenAlvIlmoitusObservable)])

    if (asiakas && edellinen) {
      const data: AlvTilienArhPaattaminenIlmoituksenPerusteellaPyynto = {
        asiakasAvain: asiakas.avain,
        ilmoitus: edellinen,
      }
      return this._firebase
        .functionsCall<AlvTilienArhPaattaminenIlmoituksenPerusteellaPyynto, void>('kirjanpitoAlvIlmoitusAutomaattikirjauksetUusiArhUudelleen', data)
        .then(result => {
          this._ladataanService.lopetaLataaminen()
          this._snackbar.open('ALV-tilit päätettiin onnistuneesti.', 'OK', { duration: 2500 })
        })
        .catch(err => {
          this._errorHandler.handleError(err)
          this._ladataanService.lopetaLataaminen()
        })
    }
  }

  private saveInflight = false
  async laheta(saaLahettaaAlvLaskun: boolean = true) {
    const alvIlmoitus = this.alvIlmoitusSubject.value
    if (!alvIlmoitus) {
      return
    }

    if (alvIlmoitus.arhSyotetty) {
      if (!alvIlmoitus.arhSyotetty.huojennus) {
        this.arhVirhe = 'Alarajahuojennus on syötetty vain osittain. Ole hyvä ja korjaa laskelma.'
        return
      }
    }

    if (this.saveInflight) {
      return
    }
    this.saveInflight = true

    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
      const kuukausi = await firstValueFrom(this.valittuKuukausiObservable)
      const aikaisemmat = await firstValueFrom(this.aikaisemmatVersiotObservable)
      const edellinenVeronMaara = await firstValueFrom(this.edellinenMaksettavaTaiPalautettavaVeroObservable)

      if (alvIlmoitus && asiakas && kuukausi && aikaisemmat && (edellinenVeronMaara || edellinenVeronMaara === 0)) {
        this.valmisteleAlvIlmoitusLahetettavaksi(alvIlmoitus, kuukausi, asiakas, aikaisemmat, edellinenVeronMaara)

        const data: AlvIlmoituksenLahetyspyynto = {
          asiakasAvain: asiakas.avain,
          valittuKuukausi: kuukausi,
          ilmoitus: alvIlmoitus,
        }

        const maksettavaMaara = this.alvLaskuLomake.maara().value()
        if (saaLahettaaAlvLaskun && this.alvLaskuLomake.send().value() && maksettavaMaara > 0.00999) {
          // Check that we're still allowed to send
          const allowed = await firstValueFrom(this.alvLaskunPerustiedotKunnossaObservable)
          if (!allowed) {
            throw new Error("Detected that ALV invoice can't be sent while sending it.")
          }

          const viitenumero = await firstValueFrom(this.alvLaskunViitenumeroObservable)
          const erapaiva = this._annaAlvLaskunErapaiva(asiakas, kuukausi)
          if (!viitenumero || !erapaiva) {
            throw new Error('Detected missing data in ALV invoice send.')
          }

          data.ilmoitus.alvLasku = {
            erapaiva: erapaiva,
            maksettavaMaara: maksettavaMaara,
            viitenumero: viitenumero,
            lahetetty: this._timestampService.now(),
          }
        }

        await this._firebase.functionsCall<AlvIlmoituksenLahetyspyynto, void>('kirjanpitoAlvIlmoitusLahetaUusi', data)

        // Get ready for new save
        const uusi = this._kirjanpitoJaettuService.annaUusiAlvilmoitus()

        uusi.alv255 = alvIlmoitus.alv255
        uusi.alv24 = alvIlmoitus.alv24
        uusi.alv135 = alvIlmoitus.alv135
        uusi.alv14 = alvIlmoitus.alv14
        uusi.alv10 = alvIlmoitus.alv10
        uusi.taveu = alvIlmoitus.taveu
        uusi.palveu = alvIlmoitus.palveu
        uusi.taveieu = alvIlmoitus.taveieu
        uusi.rak = alvIlmoitus.rak
        uusi.vahennettava = alvIlmoitus.vahennettava
        uusi.nolla = alvIlmoitus.nolla
        uusi.tavmyynniteu = alvIlmoitus.tavmyynniteu
        uusi.palvmyynniteu = alvIlmoitus.palvmyynniteu
        uusi.tavostoeu = alvIlmoitus.tavostoeu
        uusi.palvostoeu = alvIlmoitus.palvostoeu
        uusi.tavulkeu = alvIlmoitus.tavulkeu
        uusi.rakmetmyynti = alvIlmoitus.rakmetmyynti
        uusi.rakmetosto = alvIlmoitus.rakmetosto

        uusi.automaattikirjaus1763Summa = alvIlmoitus.automaattikirjaus1763Summa
        uusi.automaattikirjaus2939Summa = alvIlmoitus.automaattikirjaus2939Summa

        if (alvIlmoitus.arhSyotetty) {
          uusi.arhSyotetty = {
            huojennus: alvIlmoitus.arhSyotetty.huojennus,
            suhteutettuLiikevaihto: alvIlmoitus.arhSyotetty.suhteutettuLiikevaihto,
            vero: alvIlmoitus.arhSyotetty.vero,
          }
        }

        this.alvIlmoitusSubject.next(uusi)

        if (alvIlmoitus.arhSyotetty) {
          asetaArvo(this.arhLomake.arhVero, alvIlmoitus.arhSyotetty.vero)
          asetaArvo(this.arhLomake.arhLiikevaihto, alvIlmoitus.arhSyotetty.suhteutettuLiikevaihto)
        } else {
          asetaArvo(this.arhLomake.arhVero, null)
          asetaArvo(this.arhLomake.arhLiikevaihto, null)
        }

        this._ladataanService.lopetaLataaminen()
        this._snackbar.open('ALV-ilmoitus lähetettiin onnistuneesti.', 'OK', { duration: 2500 })
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this.saveInflight = false
    }
  }

  async lahetaUudelleenVaikkaTiedotEivatMuuttuneet() {
    const dialogData: AreYouSureDialogData = {
      rightAction: 'Lähetä',
      leftAction: 'Peruuta',
      text: 'ALV-ilmoitus (ei laskua) lähetetään uudelleen, vaikka tiedot eivät ole muuttuneet. Lähetä vain jos esim. jokin toinen toimija on ylikirjoittanut Lemonatorin datan ja halutaan tiedot uudelleen OmaVeroon.',
      header: 'ALV-ilmoituksen uudelleenlähetys ilman muutoksia',
    }

    const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())

    if (!isSure) {
      return
    }

    // Huom! Ei lähetä ALV-laskua uudelleen!
    await this.laheta(false)
  }

  async lahetaVainAlvLasku(edellinenIlmoitus: ListausAlvIlmoitus) {
    if (!edellinenIlmoitus.alvLasku) {
      if (this.saveInflight) {
        return
      }
      this.saveInflight = true

      this._ladataanService.aloitaLataaminen()

      try {
        const viitenumero = await firstValueFrom(this.alvLaskunViitenumeroObservable)
        const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
        let erapaiva = this._annaAlvLaskunErapaiva(asiakas, edellinenIlmoitus.endDate)
        if (!viitenumero || !erapaiva) {
          throw new Error('Data fault at 11.')
        }
        if (this._dateService.compareLocalDates(erapaiva, '<', this._dateService.currentLocalDate())) {
          erapaiva = this._dateService.currentLocalDate()
        }
        const maara = edellinenIlmoitus.maksettavaTaiPalautettavaVero - edellinenIlmoitus.edellinenMaksettavaTaiPalautettavaVero
        edellinenIlmoitus.alvLasku = {
          erapaiva: erapaiva,
          maksettavaMaara: maara,
          viitenumero: viitenumero,
          lahetetty: this._timestampService.now(),
        }
        const data: AlvIlmoituksenLahetyspyynto = {
          asiakasAvain: asiakas.avain,
          ilmoitus: edellinenIlmoitus,
          valittuKuukausi: edellinenIlmoitus.endDate,
        }
        await this._firebase.functionsCall<AlvIlmoituksenLahetyspyynto, void>('kirjanpitoAlvIlmoitusLahetaVainLasku', data)
      } catch (err) {
        this._errorHandler.handleError(err)
      } finally {
        this._ladataanService.lopetaLataaminen()
        this.saveInflight = false
      }
    }
  }

  private _annaAlvLaskunErapaiva(asiakas: Asiakas, kuukausi: LocalMonth): LocalDate {
    const kuunEnsimmainen: LocalDate = { year: kuukausi.year, month: kuukausi.month, day: 1 }
    const nykyinenIlmoitusjakso = this._asiakasJaettuService.annaNykyinenAlvIlmoitusjaksoPaivalle(asiakas, this._dateService.localDateToDate(kuunEnsimmainen))
    const seuraavaIlmoitusjakso = this._asiakasJaettuService.annaSeuraavaAlvIlmoitusjakso(asiakas, nykyinenIlmoitusjakso)
    const clientType: 'holvi' | 'regular' = this._kirjanpitoJaettuService.getClientTypeAsString(asiakas)
    return this._kirjanpitoJaettuService.annaAlvnErapaiva(clientType, kuukausi, nykyinenIlmoitusjakso, seuraavaIlmoitusjakso, this._dateService.currentLocalDate())
  }

  async lataaTyviTiedosto(ilmoitus: AlvIlmoitus) {
    this._ladataanService.aloitaLataaminen()

    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (asiakas) {
      const data: AlvIlmoituksenTyviTiedostonLatauspyynto = {
        asiakasAvain: asiakas.avain,
        alvIlmoitusAvain: ilmoitus.avain,
      }

      this._firebase
        .functionsCall<AlvIlmoituksenTyviTiedostonLatauspyynto, AlvIlmoituksenTyviTiedostonLatausvastaus>('kirjanpitoAlvIlmoitusLataaTyvi', data)
        .then(vastaus => {
          this._fileSaverService.saveStringAs(vastaus.tiedosto, vastaus.nimi, 'txt')
          this._ladataanService.lopetaLataaminen()
        })
        .catch(err => {
          this._errorHandler.handleError(err)
          this._ladataanService.lopetaLataaminen()
        })
    }
  }

  private valmisteleAlvIlmoitusLahetettavaksi(alvIlmoitus: AlvIlmoitus, kuukausi: LocalMonth, asiakas: Asiakas, aikaisemmat: AlvIlmoitus[], edellinenVeronMaara: number) {
    const jaksotiedot = this._kirjanpitoJaettuService.annaAlvIlmoituksenJaksotiedot(kuukausi, asiakas)
    const tunnistetiedot = this._kirjanpitoJaettuService.annaAlvilmoituksenTunnistetiedot(jaksotiedot)

    if (!tunnistetiedot) {
      throw new Error('Ei tunnistetietoja.')
    }

    alvIlmoitus.tunniste = this._kirjanpitoJaettuService.annaAlvilmoituksenTunniste(tunnistetiedot)
    alvIlmoitus.jarjestysnumero = aikaisemmat.length + 1
    alvIlmoitus.tunnistetiedot = tunnistetiedot
    alvIlmoitus.startDate = jaksotiedot.aikavali.start
    alvIlmoitus.endDate = jaksotiedot.aikavali.end
    alvIlmoitus.edellinenMaksettavaTaiPalautettavaVero = edellinenVeronMaara
  }

  kopioiArhIlmoitukselle(alvIlmoitus: AlvIlmoitus) {
    if (alvIlmoitus.arhLaskettu) {
      asetaArvo(this.arhLomake.arhLiikevaihto, alvIlmoitus.arhLaskettu.suhteutettuLiikevaihto)
      asetaArvo(this.arhLomake.arhVero, alvIlmoitus.arhLaskettu.vero)
    }
  }
}
